import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { DataTable, SearchFilterBar, type ColumnDef, type SortState } from '@shared-ui/components';
import { adminApi, type PipelineMetrics, type PipelineQualityCheckStat, type PipelineSlice } from '../../api/adminApi';
import { CheckInfo, SeverityBadge, severityRank } from './qualityCheckMeta';

type RangeDays = 1 | 3 | 7 | 14 | 30 | 90;
const RANGE_OPTIONS: { value: RangeDays; label: string }[] = [
  { value: 1, label: 'Last 1 day' },
  { value: 3, label: 'Last 3 days' },
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

/** Window end — today. Shared by the metrics query and the drill-down link so
 *  the two cannot select different windows. */
function toDate(): string {
  return new Date().toISOString().split('T')[0];
}

function fromDate(days: RangeDays): string {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return d.toISOString().split('T')[0];
}

const fmtUsd = (n: number) => `$${(n || 0).toFixed(2)}`;
const fmtPct = (n: number) => `${((n || 0) * 100).toFixed(1)}%`;
const fmtLatency = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);
const rateColor = (rate: number) => (rate >= 0.3 ? 'score-low' : rate >= 0.1 ? 'score-medium' : 'score-good');
const barColor = (ratePct: number) => (ratePct >= 30 ? '#e5484d' : ratePct >= 10 ? '#f5a623' : '#30a46c');

// Aggregate of one or more slices (prompt-level or model-level drill rows).
interface Agg {
  key: string;
  runs: number;
  primaryRuns: number;
  costSum: number;
  latencyWeighted: number;   // Σ(latencyAvg × runs) — divide by runs for weighted avg
  escalatedAwayRuns: number;
}
const emptyAgg = (key: string): Agg => ({ key, runs: 0, primaryRuns: 0, costSum: 0, latencyWeighted: 0, escalatedAwayRuns: 0 });
function addSlice(a: Agg, s: PipelineSlice): void {
  a.runs += s.runs;
  a.primaryRuns += s.primaryRuns;
  a.costSum += s.costSum;
  a.latencyWeighted += s.latencyAvgMs * s.runs;
  a.escalatedAwayRuns += s.escalatedAwayRuns;
}
const aggLatency = (a: Agg) => (a.runs > 0 ? a.latencyWeighted / a.runs : 0);
const aggRate = (a: Agg) => (a.runs > 0 ? a.escalatedAwayRuns / a.runs : 0);

interface Filters { search: string; promptType: string; promptFamily: string; promptVersion: string; model: string }
const EMPTY_FILTERS: Filters = { search: '', promptType: '', promptFamily: '', promptVersion: '', model: '' };

export function PipelineMetricsTab() {
  const navigate = useNavigate();
  const [days, setDays] = useState<RangeDays>(7);
  const [source, setSource] = useState<'production' | 'eval'>('production');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [flatSort, setFlatSort] = useState<SortState>({ sortBy: 'runs', sortOrder: 'desc' });
  const [drillSort, setDrillSort] = useState<SortState>({ sortBy: 'runs', sortOrder: 'desc' });
  const [checkSort, setCheckSort] = useState<SortState>({ sortBy: 'fireRate', sortOrder: 'desc' });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pipeline-metrics', days, source],
    queryFn: () => adminApi.getPipelineMetrics({ from: fromDate(days), to: toDate(), source }),
  });

  const metrics = data as PipelineMetrics | undefined;
  const allSlices = metrics?.slices ?? [];

  const escByCheck = useMemo(
    () => new Map((metrics?.escalationTriggers ?? []).map(t => [t.trigger, t.count])),
    [metrics],
  );
  const checkRows = useMemo(
    () => sortChecks(metrics?.qualityChecks ?? [], checkSort, escByCheck),
    [metrics, checkSort, escByCheck],
  );
  const qcChart = useMemo(
    () => (metrics?.qualityChecks ?? []).filter(q => q.present > 0).slice(0, 12)
      .map(q => ({ name: q.name, rate: Math.round(q.fireRate * 1000) / 10 })),
    [metrics],
  );

  // Cascading filter option lists, each narrowed by its parent selection.
  const typeOptions = useMemo(() => uniqueSorted(allSlices.map(s => s.promptType).filter(Boolean) as string[]), [allSlices]);
  const familyOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of allSlices) {
      if (filters.promptType && s.promptType !== filters.promptType) continue;
      seen.set(s.promptId, s.promptName || s.promptId.slice(0, 8));
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [allSlices, filters.promptType]);
  const versionOptions = useMemo(() => {
    const vs = new Set<number>();
    for (const s of allSlices) {
      if (filters.promptFamily && s.promptId !== filters.promptFamily) continue;
      if (filters.promptType && s.promptType !== filters.promptType) continue;
      vs.add(s.promptVersion);
    }
    return [...vs].sort((a, b) => b - a).map(v => ({ value: String(v), label: `v${v}` }));
  }, [allSlices, filters.promptFamily, filters.promptType]);
  const modelOptions = useMemo(() => uniqueSorted(allSlices.map(s => s.model)), [allSlices]);

  // Apply filters.
  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return allSlices.filter(s => {
      if (filters.promptType && s.promptType !== filters.promptType) return false;
      if (filters.promptFamily && s.promptId !== filters.promptFamily) return false;
      if (filters.promptVersion && String(s.promptVersion) !== filters.promptVersion) return false;
      if (filters.model && s.model !== filters.model) return false;
      if (q) {
        const hay = `${s.promptName ?? ''} ${s.promptType ?? ''} ${s.model} ${s.provider}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allSlices, filters]);

  // Flat sorted rows.
  const flatRows = useMemo(() => sortSlices(filtered, flatSort), [filtered, flatSort]);

  // Drill-down: aggregate by prompt (promptId), keep per-model sub-aggregates.
  const drillRows = useMemo(() => {
    const byPrompt = new Map<string, { promptId: string; promptName?: string; promptType?: string; versions: Set<number>; agg: Agg; byModel: Map<string, Agg> }>();
    for (const s of filtered) {
      let p = byPrompt.get(s.promptId);
      if (!p) { p = { promptId: s.promptId, promptName: s.promptName, promptType: s.promptType, versions: new Set(), agg: emptyAgg(s.promptId), byModel: new Map() }; byPrompt.set(s.promptId, p); }
      p.versions.add(s.promptVersion);
      addSlice(p.agg, s);
      let m = p.byModel.get(s.model);
      if (!m) { m = emptyAgg(s.model); p.byModel.set(s.model, m); }
      addSlice(m, s);
    }
    const rows = [...byPrompt.values()];
    return sortAgg(rows, drillSort, r => r.agg, r => r.promptName || r.promptId);
  }, [filtered, drillSort]);

  if (isLoading) return <div className="loading">Loading pipeline metrics…</div>;
  if (isError || !metrics) return <div className="empty-state">Could not load pipeline metrics.</div>;

  const { totals } = metrics;
  const overallEscalationRate = totals.runs > 0 ? totals.escalatedAwayRuns / totals.runs : 0;

  return (
    <div className="performance-content">
      {/* Window controls */}
      <div className="performance-summary pm-controls">
        <label>Range:{' '}
          <select className="pm-select" value={days} onChange={e => setDays(Number(e.target.value) as RangeDays)}>
            {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label>Source:{' '}
          <select className="pm-select" value={source} onChange={e => setSource(e.target.value as 'production' | 'eval')}>
            <option value="production">Production</option>
            <option value="eval">Eval</option>
          </select>
        </label>
        <span className="pm-window">{metrics.window.from} → {metrics.window.to}</span>
      </div>

      {/* Overview cards */}
      <div className="pm-cards">
        <StatCard label="Runs" value={totals.runs.toLocaleString()} />
        <StatCard label="Primary runs" value={totals.primaryRuns.toLocaleString()} />
        <StatCard label="Cost" value={fmtUsd(totals.costSum)} />
        <StatCard label="Escalated away" value={totals.escalatedAwayRuns.toLocaleString()} />
        <StatCard label="Escalation rate" value={fmtPct(overallEscalationRate)} tone={rateColor(overallEscalationRate)} />
      </div>

      {/* Quality checks: fire-rate chart + merged table (incl. escalation count) */}
      <div className="performance-section">
        <h3>Quality checks</h3>
        {qcChart.length === 0 ? (
          <div className="empty-state">No quality checks recorded in this window.</div>
        ) : (
          <div className="pm-chart">
            <ResponsiveContainer>
              <BarChart data={qcChart} layout="vertical" margin={{ left: 80, right: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis type="number" unit="%" tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={220} tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }} />
                <Tooltip
                  formatter={(v) => `${v}%`}
                  cursor={{ fill: 'var(--bg-tertiary)', opacity: 0.4 }}
                  contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 6, color: 'var(--text-primary)' }}
                  labelStyle={{ color: 'var(--text-primary)' }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Bar dataKey="rate">{qcChart.map((q, i) => <Cell key={i} fill={barColor(q.rate)} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <DataTable
            compact
            rowKey={q => q.name}
            data={checkRows}
            sort={checkSort}
            onSortChange={setCheckSort}
            emptyMessage="No quality checks recorded in this window."
            // Only checks that actually fired have anything to drill into.
            rowClickable={q => q.fired > 0}
            onRowClick={q => navigate(
              `/pipeline-checks/${encodeURIComponent(q.name)}?from=${fromDate(days)}&to=${toDate()}&source=${source}`,
            )}
            columns={[
              {
                key: 'name',
                header: 'Check',
                sortable: true,
                render: q => (
                  <span className="qc-name-cell">
                    {q.fired > 0 ? <span className="link-button">{q.name}</span> : q.name}
                    <CheckInfo name={q.name} />
                  </span>
                ),
              },
              { key: 'severity', header: 'Severity', sortable: true, render: q => <SeverityBadge name={q.name} /> },
              { key: 'present', header: 'Present', sortable: true, render: q => q.present.toLocaleString() },
              { key: 'fired', header: 'Fired', sortable: true, render: q => q.fired.toLocaleString() },
              { key: 'fireRate', header: 'Fire rate', sortable: true, render: q => <span className={`score-cell ${rateColor(q.fireRate)}`}>{fmtPct(q.fireRate)}</span> },
              {
                key: 'escalations',
                header: 'Escalations',
                sortable: true,
                render: q => {
                  const n = escByCheck.get(q.name) ?? 0;
                  return n > 0 ? n.toLocaleString() : '—';
                },
              },
            ] as ColumnDef<PipelineQualityCheckStat>[]}
          />
          <p className="muted qc-sev-legend">
            Severity is what a firing costs, not how often it fires: <strong>high</strong> — the served result is
            probably wrong or unusable; <strong>medium</strong> — the model contradicted itself and the grading is
            suspect; <strong>low</strong> — a soft signal, useful as a trend. Hover ⓘ for what each check detects.
          </p>
        </div>
      </div>

      {/* Shared filter bar for the two slice tables */}
      <div className="performance-section">
        <SearchFilterBar
          hideFetch
          filters={[
            { key: 'search', kind: 'text', placeholder: 'Search prompt / model…' },
            { key: 'promptType', kind: 'select', placeholder: 'All types', options: typeOptions.map(t => ({ value: t, label: t })) },
            { key: 'promptFamily', kind: 'select', placeholder: 'All prompts', options: familyOptions },
            { key: 'promptVersion', kind: 'select', placeholder: 'All versions', options: versionOptions },
            { key: 'model', kind: 'select', placeholder: 'All models', options: modelOptions.map(m => ({ value: m, label: m })) },
          ]}
          values={{ ...filters }}
          onChange={(v) => setFilters(prev => {
            const next: Filters = {
              search: v.search ?? '',
              promptType: v.promptType ?? '',
              promptFamily: v.promptFamily ?? '',
              promptVersion: v.promptVersion ?? '',
              model: v.model ?? '',
            };
            // Cascade resets: clearing/changing a parent clears its children.
            if (next.promptType !== prev.promptType) { next.promptFamily = ''; next.promptVersion = ''; }
            if (next.promptFamily !== prev.promptFamily) { next.promptVersion = ''; }
            return next;
          })}
        />
      </div>

      {/* Drill-down: prompt → models (separate table; flat table below) */}
      <div className="performance-section">
        <h3>By prompt (drill down to models)</h3>
        <DataTable
          compact
          rowKey={r => r.promptId}
          data={drillRows}
          sort={drillSort}
          onSortChange={setDrillSort}
          emptyMessage="No runs match the filters."
          columns={[
            { key: 'prompt', header: 'Prompt', sortable: true, render: r => <span className="prompt-name" title={r.promptId}>{r.promptName || r.promptId.slice(0, 8)}</span> },
            { key: 'promptType', header: 'Type', render: r => r.promptType || '—' },
            { key: 'versions', header: 'Versions', render: r => [...r.versions].sort((a, b) => b - a).map(v => `v${v}`).join(', ') },
            { key: 'models', header: 'Models', render: r => r.byModel.size },
            { key: 'runs', header: 'Runs', sortable: true, render: r => r.agg.runs.toLocaleString() },
            { key: 'primaryRuns', header: 'Primary', sortable: true, render: r => r.agg.primaryRuns.toLocaleString() },
            { key: 'costSum', header: 'Cost', sortable: true, render: r => fmtUsd(r.agg.costSum) },
            { key: 'latencyAvgMs', header: 'Avg latency', sortable: true, render: r => fmtLatency(aggLatency(r.agg)) },
            { key: 'escalatedAwayRuns', header: 'Escalated', sortable: true, render: r => r.agg.escalatedAwayRuns.toLocaleString() },
            { key: 'escalationRate', header: 'Escalation rate', sortable: true, render: r => <span className={`score-cell ${rateColor(aggRate(r.agg))}`}>{fmtPct(aggRate(r.agg))}</span> },
          ] as ColumnDef<typeof drillRows[number]>[]}
          renderExpandedRow={r => (
            <table className="dt dt--compact" style={{ margin: '0 0 0 24px', width: 'auto' }}>
              <thead><tr><th>Model</th><th>Runs</th><th>Primary</th><th>Cost</th><th>Avg latency</th><th>Escalated</th><th>Escalation rate</th></tr></thead>
              <tbody>
                {[...r.byModel.entries()].sort((a, b) => b[1].runs - a[1].runs).map(([model, m]) => (
                  <tr key={model}>
                    <td>{model}</td>
                    <td>{m.runs.toLocaleString()}</td>
                    <td>{m.primaryRuns.toLocaleString()}</td>
                    <td>{fmtUsd(m.costSum)}</td>
                    <td>{fmtLatency(aggLatency(m))}</td>
                    <td>{m.escalatedAwayRuns.toLocaleString()}</td>
                    <td><span className={`score-cell ${rateColor(aggRate(m))}`}>{fmtPct(aggRate(m))}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        />
      </div>

      {/* Flat: prompt × version × model × provider */}
      <div className="performance-section">
        <h3>By prompt × version × model × provider</h3>
        <DataTable
          compact
          rowKey={(s) => `${s.promptId}-${s.promptVersion}-${s.model}-${s.provider}`}
          data={flatRows}
          sort={flatSort}
          onSortChange={setFlatSort}
          emptyMessage="No runs match the filters."
          columns={[
            { key: 'promptName', header: 'Prompt', sortable: true, render: s => <span className="prompt-name" title={s.promptId}>{s.promptName || s.promptId.slice(0, 8)}</span> },
            { key: 'promptVersion', header: 'Ver', sortable: true, render: s => `v${s.promptVersion}` },
            { key: 'model', header: 'Model', sortable: true, render: s => s.model },
            { key: 'provider', header: 'Provider', sortable: true, render: s => <span className="provider-badge">{s.provider}</span> },
            { key: 'runs', header: 'Runs', sortable: true, render: s => s.runs.toLocaleString() },
            { key: 'primaryRuns', header: 'Primary', sortable: true, render: s => s.primaryRuns.toLocaleString() },
            { key: 'costSum', header: 'Cost', sortable: true, render: s => fmtUsd(s.costSum) },
            { key: 'latencyAvgMs', header: 'Avg latency', sortable: true, render: s => fmtLatency(s.latencyAvgMs) },
            { key: 'escalatedAwayRuns', header: 'Escalated', sortable: true, render: s => s.escalatedAwayRuns.toLocaleString() },
            { key: 'escalationRate', header: 'Escalation rate', sortable: true, render: s => <span className={`score-cell ${rateColor(s.escalationRate)}`}>{fmtPct(s.escalationRate)}</span> },
          ]}
        />
      </div>
    </div>
  );
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

function sortChecks(
  rows: PipelineQualityCheckStat[],
  sort: SortState,
  escByCheck: Map<string, number>,
): PipelineQualityCheckStat[] {
  const dir = sort.sortOrder === 'asc' ? 1 : -1;
  const val = (q: PipelineQualityCheckStat): string | number => {
    switch (sort.sortBy) {
      case 'name': return q.name;
      case 'severity': return severityRank(q.name);
      case 'present': return q.present;
      case 'fired': return q.fired;
      case 'escalations': return escByCheck.get(q.name) ?? 0;
      default: return q.fireRate;
    }
  };
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    // Checks that share a severity band read best worst-firing-first within it.
    if (av === bv) return (b.fireRate - a.fireRate);
    return (av < bv ? -1 : 1) * dir;
  });
}

function sortSlices(rows: PipelineSlice[], sort: SortState): PipelineSlice[] {
  const dir = sort.sortOrder === 'asc' ? 1 : -1;
  const val = (s: PipelineSlice): string | number => {
    switch (sort.sortBy) {
      case 'promptName': return (s.promptName || s.promptId).toLowerCase();
      case 'model': return s.model;
      case 'provider': return s.provider;
      case 'promptVersion': return s.promptVersion;
      case 'runs': return s.runs;
      case 'primaryRuns': return s.primaryRuns;
      case 'costSum': return s.costSum;
      case 'latencyAvgMs': return s.latencyAvgMs;
      case 'escalatedAwayRuns': return s.escalatedAwayRuns;
      case 'escalationRate': return s.escalationRate;
      default: return s.runs;
    }
  };
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
  });
}

function sortAgg<T>(rows: T[], sort: SortState, getAgg: (r: T) => Agg, getName: (r: T) => string): T[] {
  const dir = sort.sortOrder === 'asc' ? 1 : -1;
  const val = (r: T): string | number => {
    const a = getAgg(r);
    switch (sort.sortBy) {
      case 'prompt': return getName(r).toLowerCase();
      case 'runs': return a.runs;
      case 'primaryRuns': return a.primaryRuns;
      case 'costSum': return a.costSum;
      case 'latencyAvgMs': return aggLatency(a);
      case 'escalatedAwayRuns': return a.escalatedAwayRuns;
      case 'escalationRate': return aggRate(a);
      default: return a.runs;
    }
  };
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
  });
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="pm-card">
      <div className="pm-card-label">{label}</div>
      <div className={`pm-card-value ${tone ? `score-cell ${tone}` : ''}`}>{value}</div>
    </div>
  );
}
